<template>
  <v-container fluid>
    <v-card class="elevation-2">
      <v-toolbar color="primary" dark flat>
        <v-icon class="mr-2">mdi-shield-key</v-icon>
        <v-toolbar-title>Permisos predefinidos por rol</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="load" title="Actualizar"><v-icon>mdi-refresh</v-icon></v-btn>
      </v-toolbar>

      <v-card-text>
        <v-alert type="info" text dense>
          Son la plantilla de cada rol: se aplican a los usuarios que se den de alta con ese rol a partir de ahora.
          Los usuarios que ya existen no cambian.
        </v-alert>
      </v-card-text>

      <v-progress-linear v-if="loading" indeterminate></v-progress-linear>

      <template v-else-if="roles.length">
        <v-tabs v-model="tab" show-arrows>
          <v-tab v-for="role in roles" :key="role.value">
            {{ role.text }}
            <v-icon v-if="!role.editable" small class="ml-1">mdi-lock</v-icon>
            <v-icon v-else-if="!role.configured" small class="ml-1" color="warning" title="Sin configurar">mdi-alert-circle-outline</v-icon>
          </v-tab>
        </v-tabs>

        <v-tabs-items v-model="tab">
          <v-tab-item v-for="role in roles" :key="role.value">
            <v-card-text>
              <v-alert v-if="!role.editable" type="success" text dense>
                El Manager tiene siempre todos los permisos y pantallas. No es editable.
              </v-alert>
              <v-alert v-else-if="!role.configured" type="warning" text dense>
                Este rol aún no tiene permisos predefinidos: los usuarios nuevos con este rol empezarán sin permisos marcados.
              </v-alert>

              <v-simple-table dense>
                <thead>
                  <tr>
                    <th>Pantalla</th>
                    <th v-for="a in ACTIONS" :key="a.value" class="text-center">{{ a.text }}</th>
                    <th>Otros</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in matrix" :key="row.screen">
                    <td class="font-weight-medium">{{ row.label }}</td>
                    <td v-for="a in ACTIONS" :key="a.value" class="text-center">
                      <v-simple-checkbox v-if="row.actions[a.value]" :value="has(role, row.actions[a.value])"
                        :disabled="!role.editable" @input="toggle(role, row.actions[a.value], $event)" class="d-inline-flex"></v-simple-checkbox>
                    </td>
                    <td>
                      <v-checkbox v-for="p in row.others" :key="p" :input-value="has(role, p)" :label="actionLabel(p, row.screen)"
                        :disabled="!role.editable" dense hide-details class="mt-0 d-inline-flex mr-4"
                        @change="toggle(role, p, $event)"></v-checkbox>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>

              <div class="text-subtitle-2 mt-6 mb-2" v-if="general.length">Otros permisos</div>
              <v-row dense>
                <v-col cols="12" sm="6" md="4" v-for="p in general" :key="p">
                  <v-checkbox :input-value="has(role, p)" :label="permissionLabel(p, labels[p])" :disabled="!role.editable" dense
                    hide-details class="mt-0" @change="toggle(role, p, $event)"></v-checkbox>
                </v-col>
              </v-row>
            </v-card-text>

            <v-card-actions v-if="role.editable" class="px-4 pb-4">
              <v-spacer></v-spacer>
              <v-btn color="primary" :loading="saving === role.value" @click="save(role)">
                <v-icon left>mdi-content-save</v-icon>
                Guardar permisos de {{ role.text }}
              </v-btn>
            </v-card-actions>
          </v-tab-item>
        </v-tabs-items>
      </template>
    </v-card>
  </v-container>
</template>

<script>
import GrupoPanelService from '@/services/GrupoPanelService'
import { PERMISSION_ROLES, permissionLabel, apiData, apiError } from '@/grupo'

const ACTIONS = [
  { value: 'ver', text: 'Ver' },
  { value: 'crear', text: 'Crear' },
  { value: 'editar', text: 'Editar' },
  { value: 'eliminar', text: 'Eliminar' }
]

// Permisos que no se pueden asignar desde este panel (ni se guardan aunque el rol ya los tuviera)
const HIDDEN_PERMISSIONS = ['eliminar_grua']

// Permisos cuyo nombre no sigue <acción>_<pantalla> pero van en la casilla de una pantalla de la matriz
const PERMISSION_ALIASES = {
  agregar_usuario_normal: { screen: 'usuario', action: 'crear' }, // "Dar de alta usuarios" → Usuarios / Crear
  actualizar_usuario: { screen: 'usuario', action: 'editar' } // "Actualizar" → Usuarios / Editar
}

const SCREEN_LABELS = {
  vehiculo: 'Vehículos',
  movimiento: 'Movimientos',
  cesion: 'Cesiones',
  grua: 'Grúas',
  conductor: 'Conductores',
  ubicacion: 'Ubicaciones',
  usuario: 'Usuarios',
  todo: 'TO-DO',
  estado_proceso: 'Estados de proceso'
}

export default {
  name: 'GrupoPermisos',

  data: () => ({
    ACTIONS,
    tab: 0,
    loading: false,
    saving: null,
    roles: [],
    labels: {},
    matrix: [],
    general: []
  }),

  mounted () {
    this.load()
  },

  methods: {
    async load () {
      this.loading = true
      try {
        const data = apiData(await GrupoPanelService.permissions())
        this.buildCatalog(data.catalog)
        this.roles = PERMISSION_ROLES.map(r => ({
          value: r.value,
          text: r.text,
          editable: data.roles[r.value].editable,
          configured: data.roles[r.value].configured,
          permissions: [...data.roles[r.value].permissions]
        }))
      } catch (error) {
        this.$toast.error(apiError(error, 'No se pudieron cargar los permisos'))
      } finally {
        this.loading = false
      }
    },

    // Matriz pantalla × acción a partir de los nombres de permiso (<acción>_<pantalla>)
    buildCatalog (catalog) {
      const labels = {}
      Object.values(catalog).forEach(group => Object.assign(labels, group))
      const names = Object.keys(labels).filter(name => !HIDDEN_PERMISSIONS.includes(name))

      const screens = {}
      const ensureScreen = screen => {
        screens[screen] = screens[screen] || { screen, label: SCREEN_LABELS[screen] || this.humanize(screen), actions: {}, others: [] }
        return screens[screen]
      }
      names.forEach(name => {
        const alias = PERMISSION_ALIASES[name]
        if (alias) {
          ensureScreen(alias.screen).actions[alias.action] = name
          return
        }
        const action = ACTIONS.find(a => name.startsWith(a.value + '_'))
        if (action) {
          ensureScreen(name.slice(action.value.length + 1)).actions[action.value] = name
        }
      })

      const general = []
      names.forEach(name => {
        if (PERMISSION_ALIASES[name] || ACTIONS.some(a => name.startsWith(a.value + '_'))) return
        const screen = Object.keys(screens).find(s => name.endsWith('_' + s))
        if (screen) {
          screens[screen].others.push(name)
        } else {
          general.push(name)
        }
      })

      this.labels = labels
      this.matrix = Object.values(screens).sort((a, b) => a.label.localeCompare(b.label))
      this.general = general.sort()
    },

    permissionLabel,

    // En la fila de una pantalla basta con la acción: "aceptar_cesion" → "Aceptar"
    actionLabel (permission, screen) {
      const action = permission.slice(0, permission.length - screen.length - 1)
      return this.humanize(action)
    },

    humanize (text) {
      const t = text.replace(/_/g, ' ')
      return t.charAt(0).toUpperCase() + t.slice(1)
    },

    has (role, permission) {
      return role.permissions.includes(permission)
    },

    toggle (role, permission, value) {
      if (value && !role.permissions.includes(permission)) {
        role.permissions.push(permission)
      } else if (!value) {
        role.permissions = role.permissions.filter(p => p !== permission)
      }
    },

    async save (role) {
      this.saving = role.value
      try {
        const data = apiData(await GrupoPanelService.setRolePermissions(role.value, role.permissions.filter(p => !HIDDEN_PERMISSIONS.includes(p))))
        role.configured = true
        role.permissions = [...data.permissions]
        this.$toast.success(`Permisos de ${role.text} guardados. Se aplicarán a los usuarios nuevos con este rol`)
      } catch (error) {
        this.$toast.error(apiError(error, 'No se pudieron guardar los permisos'))
      } finally {
        this.saving = null
      }
    }
  }
}
</script>
